import { Heart, Info, MessageCircleHeart } from 'lucide-react'
import { PROGRAM_ESTIMATED_LOSS_PERCENT } from '../constants'

export const projectedWeight = (weight: number) => Math.round(weight * (1 - PROGRAM_ESTIMATED_LOSS_PERCENT / 100))

export const TrailHeroBanner = () => (
  <div className="relative flex h-[150px] w-full items-center justify-center overflow-hidden rounded-2xl bg-marketplace-200">
    <span className="absolute size-[260px] rounded-full border border-solid border-marketplace-300" />
    <span className="absolute size-[200px] rounded-full border border-solid border-marketplace-300" />
    <span className="absolute size-[140px] rounded-full bg-marketplace-300/70" />
    <span className="relative inline-flex size-[72px] items-center justify-center rounded-full bg-marketplace-800 text-white">
      <Info className="size-6" />
    </span>
    <span className="absolute left-[22%] top-[28%] inline-flex size-8 items-center justify-center rounded-full bg-white text-marketplace-900">
      <Heart className="size-4" />
    </span>
    <span className="absolute bottom-[26%] right-[18%] inline-flex size-8 items-center justify-center rounded-full bg-white text-marketplace-900">
      <MessageCircleHeart className="size-4" />
    </span>
  </div>
)

const CHART = { width: 300, height: 150, left: 18, right: 282, top: 34, bottom: 118 }

export const WeightProjectionChart = ({ weight, goal }: { weight: number; goal?: number }) => {
  const projected = projectedWeight(weight)
  const lowest = Math.min(projected, goal && goal > 0 ? goal : projected)
  const floor = lowest - Math.max(2, (weight - lowest) * 0.35)
  const y = (value: number) => CHART.top + ((weight - value) / Math.max(weight - floor, 1)) * (CHART.bottom - CHART.top)

  const xSixMonths = CHART.left + (CHART.right - CHART.left) * 0.62
  const yStart = y(weight)
  const ySix = y(projected)
  const yGoal = y(goal && goal > 0 ? goal : projected)
  const yTail = ySix + (CHART.bottom - ySix) * 0.18
  const xMid = CHART.left + (xSixMonths - CHART.left) * 0.5

  const solid = `M${CHART.left},${yStart} C${xMid},${yStart} ${xMid + 20},${ySix - 4} ${xSixMonths},${ySix}`
  const area = `${solid} L${xSixMonths},${CHART.bottom} L${CHART.left},${CHART.bottom} Z`
  const tail = `M${xSixMonths},${ySix} C${xSixMonths + 30},${ySix + 8} ${CHART.right - 40},${yTail} ${CHART.right},${yTail}`

  return (
    <svg
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      className="h-auto w-full"
      role="img"
      aria-label="Projeção de peso">
      <line x1={CHART.left} x2={CHART.right} y1={CHART.top} y2={CHART.top} stroke="#EEEEEE" />
      <line
        x1={CHART.left}
        x2={CHART.right}
        y1={(CHART.top + CHART.bottom) / 2}
        y2={(CHART.top + CHART.bottom) / 2}
        stroke="#EEEEEE"
      />
      <path d={area} fill="#E9EDDD" opacity="0.85" />
      <path d={solid} fill="none" stroke="#53632E" strokeWidth="2.2" strokeLinecap="round" />
      <path d={tail} fill="none" stroke="#53632E" strokeWidth="2" strokeDasharray="2 4" strokeLinecap="round" />
      <line
        x1={CHART.left}
        x2={CHART.right}
        y1={yGoal}
        y2={yGoal}
        stroke="#E0B63A"
        strokeWidth="1.5"
        strokeDasharray="5 4"
      />
      <circle cx={CHART.left} cy={yStart} r="4.5" fill="#fff" stroke="#53632E" strokeWidth="2" />
      <circle cx={xSixMonths} cy={ySix} r="4.5" fill="#53632E" />

      <rect x={CHART.left - 6} y={yStart - 26} width="46" height="18" rx="9" fill="#3B491F" />
      <text x={CHART.left + 17} y={yStart - 13.5} fontSize="10" fontWeight="600" fill="#fff" textAnchor="middle">
        {weight} kg
      </text>
      <rect x={xSixMonths - 28} y={ySix - 28} width="56" height="18" rx="9" fill="#3B491F" />
      <text x={xSixMonths} y={ySix - 15.5} fontSize="10" fontWeight="600" fill="#fff" textAnchor="middle">
        ~{projected} kg
      </text>
      {goal ? (
        <>
          <rect x={CHART.right - 62} y={yGoal + 5} width="62" height="16" rx="8" fill="#F8E9B5" />
          <text
            x={CHART.right - 31}
            y={yGoal + 16.5}
            fontSize="9.5"
            fontWeight="600"
            fill="#6A5200"
            textAnchor="middle">
            Meta {goal} kg
          </text>
        </>
      ) : null}

      <text x={CHART.left} y={CHART.height - 6} fontSize="9.5" fill="#6B6560">
        Hoje
      </text>
      <text x={xMid + 10} y={CHART.height - 6} fontSize="9.5" fill="#6B6560" textAnchor="middle">
        3 meses
      </text>
      <text x={xSixMonths} y={CHART.height - 6} fontSize="9.5" fill="#6B6560" textAnchor="middle">
        6 meses
      </text>
    </svg>
  )
}
